"use no memo";

import React, {
  forwardRef,
  CSSProperties,
  ReactNode,
  Dispatch,
  useState,
} from "react";
import classNames from "classnames";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { Header, HeaderGroup } from "@tanstack/react-table";
import { TableContext } from "src/app/project/TableField/types";
import { SkeletonText } from "src/app/components/Skeleton/Skeleton";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ProjectFieldPreviewSwitch } from "src/app/project/TableField/components/ProjectFieldPreviewSwitch";
import { TableSearchResultsItem } from "src/app/components/TableSearch/types";
import { HeaderCell, SKELETON_BLACKLIST } from "../HeaderCell";
import getCommonPinningStyles from "../functions";
import Cell from "../Cell";

export interface TableVirtuosoContext {
  table: any;
  handleRowDragEnd: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void;
  handleColumnDragEnd: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => Promise<void>;
  setIsDragging: Dispatch<React.SetStateAction<boolean>>;
  tasksLoading: boolean;
  tableContext?: TableContext;
  totalRowCount?: number;
  highlightedCellKeys?: Set<string>;
  activeResult?: TableSearchResultsItem | null;
  selectedRowIds?: string[];
  selectedRowsTotalRow?: Record<string, any>;
}

interface DraggableRowProps {
  children: ReactNode;
  ["data-index"]: number;
  ["data-item-index"]: number;
  ["data-known-size"]: number;
  item: any;
  row: any;
  rowIndex: number;
  style: CSSProperties;
  context: TableVirtuosoContext;
}

export const DraggableRow = SortableElement((props: DraggableRowProps) => {
  const { row, rowIndex, context, item, ...rest } = props;

  const visibleCells = row.getVisibleCells();
  const cellsCount = visibleCells.length;

  const isSelected = context.selectedRowIds?.includes(row.id);

  return (
    // connect row ref to dnd-kit, apply important styles
    <tr
      className={classNames({ "highlighted-row": isSelected })}
      {...rest}
      data-task-title={row?.original?.title}
    >
      {visibleCells.map((cell: any, cellIndex: number) => {
        // Data columns start after drag-handle + select (offset 2)
        const COLUMN_OFFSET = 2;
        const dataColumnIndex = cellIndex - COLUMN_OFFSET;

        const cellKey = `${rowIndex}-${dataColumnIndex}`;
        const highlighted = context.highlightedCellKeys?.has(cellKey) ?? false;

        const { activeResult } = context;
        const active =
          activeResult?.columnIndex === dataColumnIndex &&
          activeResult?.rowIndex === rowIndex;

        return (
          <td
            className={classNames("tasks-table__td", {
              "tasks-table__td--in-draggable-column":
                cellIndex > 1 && cellIndex < cellsCount - 3,
              "tasks-table__td--action": cell.column.id === " action",
              "tasks-table__td--highlighted": highlighted,
              "tasks-table__td--highlighted-active": active,
            })}
            style={{
              ...getCommonPinningStyles(cell.column),
            }}
          >
            {cell.column.id === "filler" ? null : (
              <Cell
                key={cell.id}
                cell={cell}
                rowData={cell.row.original}
                rowId={row.id}
                rowIndex={rowIndex}
                tableContext={context.tableContext}
                tasksLoading={context.tasksLoading}
              />
            )}
          </td>
        );
      })}
    </tr>
  );
});

export function VirtuosoTable(props: any) {
  const { style, context, ...rest } = props;
  return (
    <table
      {...rest}
      style={{
        ...style,
        width: "100%",
        tableLayout: "auto",
        borderCollapse: "collapse",
        borderSpacing: 1,
      }}
    />
  );
}

const DraggableHeaderCell = SortableElement<{
  header: Header<any, any>;
  tasksLoading: boolean;
  tableContext: TableContext | undefined;
  draggedColumnId: string | null;
}>(
  (props: {
    header: Header<any, any>;
    tasksLoading: boolean;
    tableContext: TableContext | undefined;
    draggedColumnId: string | null;
  }) => {
    const { header, tasksLoading, tableContext, draggedColumnId } = props;

    const isBeingDragged = draggedColumnId === header.column.id;

    return (
      <HeaderCell
        key={header.id}
        header={header}
        columnIndex={header.index}
        tasksLoading={tasksLoading}
        tableContext={tableContext}
        draggedColumnId={draggedColumnId}
        isBeingDragged={isBeingDragged}
        draggable
      />
    );
  },
);

const TableHeadRowContext = SortableContainer<{
  headerGroup: HeaderGroup<any>;
  tasksLoading: boolean;
  totalRowCount: number | undefined;
  currentRowCount: number;
  tableContext: TableContext | undefined;
  draggedColumnId: string | null;
}>(
  (props: {
    headerGroup: HeaderGroup<any>;
    tasksLoading: boolean;
    totalRowCount: number | undefined;
    currentRowCount: number;
    tableContext: TableContext | undefined;
    draggedColumnId: string | null;
  }) => {
    const {
      headerGroup,
      tasksLoading,
      totalRowCount,
      currentRowCount,
      tableContext,
      draggedColumnId,
    } = props;

    const headersFirstThree = headerGroup.headers.slice(0, 3);
    const headersRemaining = headerGroup.headers.slice(
      3,
      headerGroup.headers.length,
    );

    return (
      <tr className="tasks-table__column-dragging-context" key="header-group">
        {headersFirstThree.map((header: Header<any, any>) => {
          return (
            <HeaderCell
              key={header.id}
              header={header}
              columnIndex={header.index}
              tasksLoading={tasksLoading}
              totalRowCount={totalRowCount}
              currentRowCount={currentRowCount}
              tableContext={tableContext}
            />
          );
        })}

        {headersRemaining.map((header: Header<any, any>, index: number) => {
          const indexDiff = tableContext === "workspaceTasksList" ? 3 : 4;

          if (index + indexDiff >= headerGroup.headers.length) {
            return (
              <HeaderCell
                key={header.id}
                header={header}
                columnIndex={header.index}
                tasksLoading={tasksLoading}
                tableContext={tableContext}
              />
            );
          }

          return (
            <DraggableHeaderCell
              key={`header-${header.id}`}
              index={index}
              header={header}
              tasksLoading={tasksLoading}
              tableContext={tableContext}
              draggedColumnId={draggedColumnId}
            />
          );
        })}
      </tr>
    );
  },
);

function TotalsHeaderRow({
  headerGroup,
  tasksLoading,
  selectedRowsTotalRow,
  tableContext,
}: {
  headerGroup: HeaderGroup<any>;
  tasksLoading: boolean;
  selectedRowsTotalRow: Record<string, any> | undefined;
  tableContext: TableContext | undefined;
}) {
  const headersFirstThree = headerGroup.headers.slice(0, 3);
  const headersRemaining = headerGroup.headers.slice(
    3,
    headerGroup.headers.length,
  );

  const hasAnyTotalRowObjects = Boolean(
    selectedRowsTotalRow && Object.keys(selectedRowsTotalRow).length > 0,
  );

  // Data Hub only: temporary fix. Keep transparent because the two-row totals
  // layout can cover row borders. The parent already provides the white background.
  const isTransparentBg = tableContext === "workspaceTasksList";

  return (
    <tr
      className={classNames("tasks-table__totals-row", {
        "tasks-table__totals-row--hidden": !hasAnyTotalRowObjects,
      })}
    >
      {headersFirstThree.map((header: Header<any, any>, index: number) => {
        if (index === headersFirstThree.length - 1) {
          return (
            <th
              key={header.id}
              className={classNames("tasks-table__th", {
                "tasks-table__th--transparent-bg": isTransparentBg,
              })}
              style={{
                ...getCommonPinningStyles(header.column),
              }}
            >
              {tasksLoading ? (
                !SKELETON_BLACKLIST.includes(header?.column.id) && (
                  <>
                    <span style={{ visibility: "hidden" }} />
                    <SkeletonText width={60} height={15} />
                  </>
                )
              ) : (
                <div
                  className="tasks-table__header-wrapper"
                  data-column-name="total"
                  data-column-key="total"
                >
                  <IDHFormattedMessage id="ws_total" defaultMessage="Total" />
                </div>
              )}
            </th>
          );
        }

        return (
          <th
            className={classNames("tasks-table__th", {
              "tasks-table__th--transparent-bg": isTransparentBg,
            })}
            key={header.id}
            style={{
              ...getCommonPinningStyles(header.column),
            }}
          >
            <div className="tasks-table__header-wrapper">&nbsp;</div>
          </th>
        );
      })}

      {headersRemaining.map((header: Header<any, any>) => {
        const totalsForColumn: any[] =
          selectedRowsTotalRow?.[header.column.id] ?? [];
        if (totalsForColumn.length > 0) {
          return (
            <th
              key={header.id}
              className={classNames(
                "tasks-table__th",
                "tasks-table__th--total-row",
                {
                  "tasks-table__th--transparent-bg": isTransparentBg,
                },
              )}
              style={{
                ...getCommonPinningStyles(header.column),
              }}
            >
              {tasksLoading ? (
                <>
                  <span style={{ visibility: "hidden" }} />
                  <SkeletonText width={60} height={15} />
                </>
              ) : (
                <div className="tasks-table__totals-stack">
                  {totalsForColumn.map((total) => (
                    <ProjectFieldPreviewSwitch
                      key={total.currencyCode ?? "default"}
                      data={{ data: { currencyCode: total.currencyCode } }}
                      fieldType={total.type}
                      value={total.value}
                      prefix={total.currencyCode}
                    />
                  ))}
                </div>
              )}
            </th>
          );
        }
        return (
          <th
            key={header.id}
            className={classNames("tasks-table__th", {
              "tasks-table__th--action": header.column.id === " action",
              "tasks-table__th--transparent-bg": isTransparentBg,
            })}
            style={{
              ...getCommonPinningStyles(header.column),
            }}
          >
            &nbsp;
          </th>
        );
      })}
    </tr>
  );
}

export const VirtuosoTableHead = forwardRef((props: any, ref: any) => {
  const { context, style, ...rest } = props;

  const {
    handleColumnDragEnd,
    setIsDragging,
    table,
    tasksLoading,
    tableContext,
    totalRowCount,
    selectedRowsTotalRow,
  }: TableVirtuosoContext = context;

  const [draggedColumnId, setDraggedColumnId] = useState(null);

  const headerGroup = table.getHeaderGroups()[0];

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    setDraggedColumnId(null);
    handleColumnDragEnd({ oldIndex, newIndex });
  };

  const updateBeforeSortStart = ({ index }: { index: number }) => {
    setIsDragging(true);
    const columnId = headerGroup.headers[index + 3].column.id;
    setDraggedColumnId(columnId);
  };

  return (
    <thead
      ref={ref}
      style={{
        ...style,
        top: tableContext === "workspaceTasksList" ? 213 : 120,
      }}
      {...rest}
    >
      <TableHeadRowContext
        axis="x"
        lockAxis="x"
        helperClass="tasks-table__column-dragged-item"
        helperContainer={
          (document.querySelector(
            ".tasks-table__column-dragging-context",
          ) as HTMLElement) || undefined
        }
        onSortEnd={onSortEnd}
        headerGroup={headerGroup}
        tasksLoading={tasksLoading}
        totalRowCount={totalRowCount}
        currentRowCount={table.getRowCount()}
        tableContext={tableContext}
        draggedColumnId={draggedColumnId}
        updateBeforeSortStart={updateBeforeSortStart}
        useDragHandle
      />
      <TotalsHeaderRow
        headerGroup={headerGroup}
        tasksLoading={tasksLoading}
        selectedRowsTotalRow={selectedRowsTotalRow}
        tableContext={tableContext}
      />
    </thead>
  );
});

const TableBodyContext = SortableContainer(({ listRef, ...props }: any) => {
  return <tbody ref={listRef} {...props} />;
});

export const VirtuosoTableBody = forwardRef((props: any, ref: any) => {
  const { context, ...rest } = props;

  const { handleRowDragEnd } = context;

  return (
    <TableBodyContext
      {...rest}
      listRef={ref}
      onSortEnd={handleRowDragEnd}
      useDragHandle
    />
  );
});

export function VirtuosoTableRow(props: any) {
  const { item: row } = props;

  const index = props["data-index"];

  return (
    <DraggableRow
      key={row.id}
      index={index}
      rowIndex={index}
      row={row}
      {...props}
    />
  );
}
